import axios from "axios"
import { Logger, funcName } from "packs/common"
import NotificationControl from "packs/utils/NotificationControl"
import Util from "packs/utils/Util"

export default class MarketingCampaignFetcher {
    static readonly basePath = "/api/marketing/campaigns"

    static fetchCampaigns(params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} params:${Util.output(params)}`)
        return axios
            .get(this.basePath, { params })
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                if (err.response?.data?.read_only) {
                    return {
                        ...err.response.data,
                        campaigns: [],
                        total: 0,
                        page: 1,
                        per: 30,
                        has_next: false,
                    }
                }
                return null
            })
    }

    static createCampaign(params: any): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} params:${Util.output(params)}`)
        return axios
            .post(this.basePath, { campaign: params })
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static syncRemote(params: any = {}): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} requested has_api_key:${!!params?.api_key}`
        )
        return axios
            .post(`${this.basePath}/sync_remote`, params)
            .then(res => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} succeeded status:${res.status || 200}`
                )
                return res.data
            })
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchPublicRooms(campaignId: string, params: any = {}): Promise<any> {
        return axios
            .get(`${this.basePath}/${campaignId}/public_room_links`, { params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGoal(campaignId: string): Promise<any> {
        return axios
            .get(`${this.basePath}/${campaignId}/goal`)
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                throw err
            })
    }

    static saveGoal(campaignId: string, promptText: string, expectedVersion: number): Promise<any> {
        return axios
            .put(`${this.basePath}/${campaignId}/goal`, {
                goal: { prompt_text: promptText, expected_version: expectedVersion },
            })
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                throw err
            })
    }

    static clearGoal(campaignId: string, expectedVersion: number): Promise<any> {
        return axios
            .delete(`${this.basePath}/${campaignId}/goal`, {
                data: { goal: { expected_version: expectedVersion } },
            })
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                throw err
            })
    }

    static attachPublicRooms(campaignId: string, params: any): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/public_room_links`, params)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static syncPublicRooms(campaignId: string, linkIds: string[] = [], params: any = {}): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/public_room_links/sync`, {
                ...params,
                ...(linkIds.length ? { link_ids: linkIds } : {}),
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static removePublicRoom(campaignId: string, linkId: string): Promise<any> {
        return axios
            .delete(`${this.basePath}/${campaignId}/public_room_links/${linkId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static updateCampaign(id: string, params: any): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id} params:${Util.output(params)}`)
        return axios
            .put(`${this.basePath}/${id}`, { campaign: params })
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    // 呼び出し側で409・422の内容を表示するため、エラーは通知せずそのまま投げる。
    static updatePlanning(id: string, params: any, expectedVersion: number): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .patch(`${this.basePath}/${id}/planning`, { planning: { ...params, expected_version: expectedVersion } })
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                throw err
            })
    }

    // トップの設定チェックリスト用。取得できなくても一覧は使えるため通知しない。
    static fetchPlanningStatus(id: string): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .get(`${this.basePath}/${id}/planning`)
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                return null
            })
    }

    static fetchSummary(id: string, params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id} params:${Util.output(params)}`)
        return axios
            .get(`${this.basePath}/${id}/summary`, { params })
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchEvents(id: string, params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id} params:${Util.output(params)}`)
        return axios
            .get(`${this.basePath}/${id}/events`, { params })
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGa4Dashboard(id: string, params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .get(`${this.basePath}/${id}/ga4/dashboard`, { params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGscDashboard(id: string, params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .get(`${this.basePath}/${id}/gsc/dashboard`, { params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGscGa4Insights(id: string, params: any = {}): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .get(`${this.basePath}/${id}/gsc/ga4_insights`, { params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchIntegrations(campaignId: string): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId}`)
        return axios
            .get(`${this.basePath}/${campaignId}/integrations`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    // 設定ウィザード用。未保存の接続に計測scopeの提案値を付けて返す。失敗しても入力は続けられるため通知しない。
    static fetchIntegrationScopeSuggestions(campaignId: string): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId}`)
        return axios
            .get(`${this.basePath}/${campaignId}/integrations`, { params: { include_suggestions: 1 } })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return null
            })
    }

    static createIntegration(campaignId: string, params: any): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId}`)
        return axios
            .post(`${this.basePath}/${campaignId}/integrations`, { integration: params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static updateIntegration(campaignId: string, integrationId: string, params: any): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .patch(`${this.basePath}/${campaignId}/integrations/${integrationId}`, {
                integration: params,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static updateIntegrationScope(campaignId: string, integrationId: string, measurementScope: any): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .patch(`${this.basePath}/${campaignId}/integrations/${integrationId}/scope`, {
                measurement_scope: measurementScope,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static importGenerativeAiPerformance(
        campaignId: string,
        integrationId: string,
        file: File,
        periodStart?: string,
        periodEnd?: string
    ): Promise<any> {
        const form = new FormData()
        form.append("file", file)
        if (periodStart) form.append("period_start", periodStart)
        if (periodEnd) form.append("period_end", periodEnd)
        return axios
            .post(`${this.basePath}/${campaignId}/integrations/${integrationId}/generative_ai_import`, form)
            .then(res => res.data)
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static pauseIntegration(campaignId: string, integrationId: string): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .patch(`${this.basePath}/${campaignId}/integrations/${integrationId}/pause`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static disconnectIntegration(campaignId: string, integrationId: string): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .delete(`${this.basePath}/${campaignId}/integrations/${integrationId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static connectWordpress(campaignId: string, params: any): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId}`)
        return axios
            .post(`${this.basePath}/${campaignId}/wordpress_connections`, {
                wordpress_connection: params,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static reauthenticateWordpress(
        campaignId: string,
        integrationId: string,
        params: any
    ): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .patch(
                `${this.basePath}/${campaignId}/wordpress_connections/${integrationId}`,
                { wordpress_connection: params }
            )
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static disconnectWordpress(campaignId: string, integrationId: string): Promise<any> {
        Logger(
            `MarketingCampaignFetcher.${funcName()} campaign_id:${campaignId} integration_id:${integrationId}`
        )
        return axios
            .delete(`${this.basePath}/${campaignId}/wordpress_connections/${integrationId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static auditWordpressAiSearchReadiness(
        campaignId: string,
        integrationId: string,
        url?: string
    ): Promise<any> {
        Logger(
            "MarketingCampaignFetcher." + funcName() +
                " campaign_id:" + campaignId + " integration_id:" + integrationId
        )
        return axios
            .post(
                this.basePath + "/" + campaignId + "/wordpress_connections/" +
                    integrationId + "/audit_ai_search_readiness",
                url ? { audit_url: url } : {}
            )
            .then(res => res.data)
            .catch(err => {
                Logger(
                    "MarketingCampaignFetcher." + funcName() +
                        " failed status:" + (err.response?.status || "unknown")
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchWordpressMedia(campaignId: string, integrationId: string): Promise<any> {
        Logger(
            "MarketingCampaignFetcher." + funcName() +
                " campaign_id:" + campaignId + " integration_id:" + integrationId
        )
        return axios
            .get(this.basePath + "/" + campaignId + "/wordpress_media", {
                params: { integration_id: integrationId },
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    "MarketingCampaignFetcher." + funcName() +
                        " failed status:" + (err.response?.status || "unknown")
                )
                return {
                    error: true,
                    message:
                        err.response?.data?.message ||
                        "WordPressメディア情報を取得できませんでした。",
                    error_classification:
                        err.response?.data?.error_classification || "request_failed",
                }
            })
    }

    static createArticleProposal(campaignId: string, params: any): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/articles`, { article: params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static updateArticleProposal(campaignId: string, articleId: string, params: any): Promise<any> {
        return axios
            .patch(`${this.basePath}/${campaignId}/articles/${articleId}`, { article: params })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchArticleProposal(campaignId: string, articleId: string): Promise<any> {
        return axios
            .get(`${this.basePath}/${campaignId}/articles/${articleId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return null
            })
    }

    static fetchArticleProposals(campaignId: string, cursor?: string): Promise<any> {
        const request = cursor
            ? axios.get(`${this.basePath}/${campaignId}/articles`, { params: { cursor } })
            : axios.get(`${this.basePath}/${campaignId}/articles`)
        return request
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return null
            })
    }

    static archiveArticleProposal(campaignId: string, articleId: string): Promise<any> {
        return axios
            .delete(`${this.basePath}/${campaignId}/articles/${articleId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static approveArticleProposal(campaignId: string, articleId: string, revisionInstruction?: string): Promise<any> {
        return axios
            .post(
                `${this.basePath}/${campaignId}/articles/${articleId}/approve`,
                revisionInstruction === undefined
                    ? undefined
                    : { article: { revision_instruction: revisionInstruction } }
            )
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static generateArticleDraft(campaignId: string, articleId: string): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/articles/${articleId}/generate`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static reviewArticlePublication(
        campaignId: string,
        articleId: string,
        migration?: { confirmation: boolean; reason: string }
    ): Promise<any> {
        return this.articlePublicationRequest(
            campaignId,
            articleId,
            "publication_review",
            migration
                ? {
                    quality_plan_migration_confirmation: migration.confirmation,
                    quality_plan_migration_reason: migration.reason,
                }
                : undefined
        )
    }

    static adoptWordpressArticle(campaignId: string, articleId: string): Promise<any> {
        return this.articlePublicationRequest(campaignId, articleId, "adopt_wordpress")
    }

    static publishArticle(
        campaignId: string,
        articleId: string,
        publicationPreflightDigest: string
    ): Promise<any> {
        return this.articlePublicationRequest(campaignId, articleId, "publish", {
            confirmation: true,
            publication_preflight_digest: publicationPreflightDigest,
        })
    }

    static recheckArticleSeo(campaignId: string, articleId: string): Promise<any> {
        return this.articlePublicationRequest(campaignId, articleId, "recheck_seo")
    }

    static fetchArticlePublicationOutcomes(campaignId: string, articleId: string): Promise<any> {
        return axios
            .get(`${this.basePath}/${campaignId}/articles/${articleId}/publication_outcomes`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return null
            })
    }

    static createArticleSearchObservation(
        campaignId: string,
        articleId: string,
        observation: any
    ): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/articles/${articleId}/search_observations`, {
                observation,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return {
                    error: true,
                    message: err.response?.data?.message || "検索結果の標本観測を保存できませんでした。",
                }
            })
    }

    private static articlePublicationRequest(
        campaignId: string,
        articleId: string,
        action: string,
        params: any = {}
    ): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/articles/${articleId}/${action}`, params)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                return {
                    error: true,
                    message: err.response?.data?.message || "記事の公開処理を完了できませんでした。",
                    error_classification:
                        err.response?.data?.error_classification || "request_failed",
                }
            })
    }

    static uploadWordpressMedia(
        campaignId: string,
        integrationId: string,
        params: any
    ): Promise<any> {
        Logger(
            "MarketingCampaignFetcher." + funcName() +
                " campaign_id:" + campaignId + " integration_id:" + integrationId
        )
        const formData = new FormData()
        formData.append("integration_id", integrationId)
        Object.entries(params).forEach(([key, value]) => {
            formData.append(
                "wordpress_media[" + key + "]",
                value instanceof Blob ? value : String(value ?? "")
            )
        })
        return axios
            .post(this.basePath + "/" + campaignId + "/wordpress_media", formData)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    "MarketingCampaignFetcher." + funcName() +
                        " failed status:" + (err.response?.status || "unknown")
                )
                return {
                    error: true,
                    message:
                        err.response?.data?.message ||
                        "WordPressへ画像をアップロードできませんでした。",
                    error_classification:
                        err.response?.data?.error_classification || "request_failed",
                    media: err.response?.data?.media,
                    warnings: err.response?.data?.warnings,
                }
            })
    }

    static updateWordpressMedia(
        campaignId: string,
        mediaId: string,
        params: any
    ): Promise<any> {
        Logger(
            "MarketingCampaignFetcher." + funcName() +
                " campaign_id:" + campaignId + " media_id:" + mediaId
        )
        return axios
            .patch(this.basePath + "/" + campaignId + "/wordpress_media/" + mediaId, {
                wordpress_media: params,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    "MarketingCampaignFetcher." + funcName() +
                        " failed status:" + (err.response?.status || "unknown")
                )
                return {
                    error: true,
                    message:
                        err.response?.data?.message ||
                        "WordPressメディア情報を更新できませんでした。",
                    error_classification:
                        err.response?.data?.error_classification || "request_failed",
                }
            })
    }

    static beginGa4Oauth(campaignId: string, credentialId?: string): Promise<any> {
        return this.beginGoogleOauth("ga4", campaignId, credentialId)
    }

    static beginGscOauth(campaignId: string, credentialId?: string): Promise<any> {
        return this.beginGoogleOauth("gsc", campaignId, credentialId)
    }

    private static beginGoogleOauth(
        service: "ga4" | "gsc",
        campaignId: string,
        credentialId?: string
    ): Promise<any> {
        return axios
            .post("/api/marketing/google/oauth/start", {
                campaign_id: campaignId,
                service,
                ...(credentialId ? { credential_id: credentialId } : {}),
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGscSites(campaignId: string, credentialId: string): Promise<any> {
        return axios
            .get("/api/marketing/google/search_console/sites", {
                params: {
                    campaign_id: campaignId,
                    credential_id: credentialId,
                },
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static connectGscSites(
        campaignId: string,
        credentialId: string,
        siteUrls: string[]
    ): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/gsc/sites`, {
                credential_id: credentialId,
                site_urls: siteUrls,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static fetchGa4Properties(
        campaignId: string,
        credentialId: string,
        pageToken?: string
    ): Promise<any> {
        return axios
            .get("/api/marketing/google/analytics/properties", {
                params: {
                    campaign_id: campaignId,
                    credential_id: credentialId,
                    ...(pageToken ? { page_token: pageToken } : {}),
                },
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static connectGa4Properties(
        campaignId: string,
        credentialId: string,
        propertyIds: string[]
    ): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/ga4/properties`, {
                credential_id: credentialId,
                property_ids: propertyIds,
            })
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static syncIntegration(
        campaignId: string,
        integrationId: string,
        params: { full_refresh?: boolean } = {}
    ): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/integrations/${integrationId}/sync`, params)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static syncAllIntegrations(campaignId: string, params: any = {}): Promise<any> {
        return axios
            .post(`${this.basePath}/${campaignId}/integrations/sync_all`, params)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static revokeGa4Credential(credentialId: string): Promise<any> {
        return this.revokeGoogleCredential(credentialId)
    }

    static revokeGoogleCredential(credentialId: string): Promise<any> {
        return axios
            .delete(`/api/marketing/google/oauth/credentials/${credentialId}`)
            .then(res => res.data)
            .catch(err => {
                Logger(
                    `MarketingCampaignFetcher.${funcName()} failed status:${err.response?.status || "unknown"}`
                )
                NotificationControl.showErrorMessage(err)
                return null
            })
    }

    static archiveCampaign(id: string): Promise<any> {
        Logger(`MarketingCampaignFetcher.${funcName()} id:${id}`)
        return axios
            .delete(`${this.basePath}/${id}`)
            .then(res => {
                Logger(`MarketingCampaignFetcher.${funcName()} result:${Util.output(res.data)}`)
                return res.data
            })
            .catch(err => {
                Logger(`MarketingCampaignFetcher.${funcName()} err:${err.message} ${Util.output(err.response)}`)
                NotificationControl.showErrorMessage(err)
                return null
            })
    }
}
